import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Shimmer from 'react-shimmer-effect';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import {
  format, addDays, startOfDay, isAfter, isSameDay, compareAsc, parseISO,
} from 'date-fns';

import Button from '../../../../shared/_TBComponents/Button';
import Icon from '../../../../shared/_TBComponents/Icon';
import Text from '../../../../shared/_TBComponents/Text';
import PreviewCalendarList from './PreviewCalendarList';
import PreviewProgramList from './PreviewProgramList';
import {
  fetchProgramItems,
  fetchProgramDayOne,
  fetchWorkoutItemsByDate,
  fetchCalendarPreview,
  setIsPreviewCarouselShowing,
} from '../../ducks/navigatorActions';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  height: 100%;
  background: #ffffff;
  border-radius: 4px;
  padding: 16px;
  gap: 16px;
  overflow: hidden;
  box-shadow: 0 16px 24px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05);
  border: 1px solid #eee;

  @media screen and (max-width: 1200px) {
    min-height: 250px;
  }

  @media screen and (max-width: 767px) {
    overflow: visible;
    height: auto;
    min-height: 0;
    flex: none;
  }
`;

const ScrollContainer = styled.div`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;

  @media screen and (max-width: 767px) {
    overflow-y: visible;
    height: auto;
  }

  &::-webkit-scrollbar {
    width: 8px;
    opacity: 0;
    transition: opacity 0.3s;
  }
  &:hover::-webkit-scrollbar {
    opacity: 1;
  }
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #999;
  }

  scrollbar-width: thin;
  scrollbar-color: #ccc #f1f1f1;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
`;

const TitleContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const WorkoutContent = styled.div`
  display: flex;
  flex-direction: column;
  
  &:first-of-type {
    flex: 1;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 250px;
  padding: 0 64px;
  text-align: center;

  @media screen and (max-width: 1200px) {
    min-height: 150px;
  }
`;

const EmptyIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 8px;
`;

const LoadingTitle = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 240px;
  min-height: 30px;
  border-radius: 24px;
`;

const AthletePreview = ({ selectedAthlete }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const currentWorkout = useSelector((state) => state.navigator?.data?.currentWorkout);
  const workoutCalendar = useSelector((state) => state.navigator?.data?.calendarPreview);
  const calendarPreviewError = useSelector((state) => state.navigator?.data?.calendarPreviewError);
  const dayWorkoutItemsError = useSelector((state) => state.navigator?.data?.dayWorkoutItemsError);
  const programItems = useSelector((state) => state.navigator.data.programItems);
  const workoutItems = useSelector(
    (state) => state.navigator?.data?.currentWorkout?.workoutItems || [],
  );

  const isCalendarPreviewLoading = useSelector(
    (state) => state.navigator?.ui?.isLoadingCalendarPreview,
  );
  const isProgramItemsLoading = useSelector(
    (state) => state.navigator.ui.isLoadingProgramItems,
  );
  const isWorkoutsListLoading = useSelector(
    (state) => state.navigator?.ui?.isLoadingWorkoutsList,
  );

  const activePrograms = [...(currentWorkout?.assignedDayBasedProgramsData ?? []),
    ...(currentWorkout?.activeDayBasedProgramsData ?? [])];

  const initialMountRef = React.useRef(true);

  const [expandedProgramKey, setExpandedProgramKey] = useState(null);
  const [nextWorkoutDate, setNextWorkoutDate] = useState(null);
  // Keep preview tile stable so carousel fetches do not mess with it
  const [previewWorkoutItems, setPreviewWorkoutItems] = useState([]);
  const [previewActivePrograms, setPreviewActivePrograms] = useState([]);

  const hasNoPreviewItems = !previewWorkoutItems.length && !previewActivePrograms.length;
  const previewErrorMessage = dayWorkoutItemsError || calendarPreviewError;
  const hasPreviewError = Boolean(previewErrorMessage);
  const showNextWorkoutLoading = !!selectedAthlete?.id
    && (isWorkoutsListLoading || isCalendarPreviewLoading);

  // Resets preview to prevent unwanted stale data
  useEffect(() => {
    setPreviewWorkoutItems([]);
    setPreviewActivePrograms([]);
  }, [selectedAthlete?.id]);

  // Fetch workout calendar for the selected athlete
  useEffect(() => {
    if (selectedAthlete && Object.keys(currentUser).length) {
      const startDate = format(new Date(), 'yyyy-MM-dd');
      const endDate = format(addDays(new Date(), 28), 'yyyy-MM-dd');
      dispatch(
        fetchCalendarPreview(
          currentUser,
          selectedAthlete.id,
          startDate,
          endDate,
          false,
        ),
      );
    }
  }, [selectedAthlete?.id, currentUser?.accountCode, dispatch]);

  // Find the next scheduled workout date
  useEffect(() => {
    if (workoutCalendar && workoutCalendar.length) {
      const today = startOfDay(new Date());

      const upcomingWorkouts = workoutCalendar
        .filter((item) => {
          const hasWorkout = item.statusDescription && item.statusDescription !== 'NO_WORKOUT';
          const itemDate = startOfDay(parseISO(item.date));
          const isFutureOrToday = isAfter(itemDate, today) || isSameDay(itemDate, today);

          return hasWorkout && isFutureOrToday;
        })
        .sort((a, b) => compareAsc(parseISO(a.date), parseISO(b.date)));

      if (upcomingWorkouts.length > 0) {
        setNextWorkoutDate(upcomingWorkouts[0].date);
      } else {
        setNextWorkoutDate(null);
      }
    }
  }, [workoutCalendar]);

  // Fetch workout items for next scheduled workout date
  useEffect(() => {
    if (selectedAthlete && Object.keys(currentUser).length) {
      const today = format(new Date(), 'yyyy-MM-dd');
      dispatch(
        fetchWorkoutItemsByDate(
          currentUser,
          selectedAthlete.id,
          today,
          false,
          false,
          { useNavigatorDate: true },
        ),
      );
    }
  }, [selectedAthlete?.id, currentUser?.accountCode, dispatch]);

  // Keeps original preview data and prevents overwriting with program session fetches
  useEffect(() => {
    if (initialMountRef.current) {
      initialMountRef.current = false;
      return;
    }

    const isProgramSessionPayload = Boolean(currentWorkout?.dayBasedProgramId)
      && !Object.prototype.hasOwnProperty.call(currentWorkout || {}, 'activeDayBasedProgramsData');

    if (isProgramSessionPayload) return;

    if (!previewWorkoutItems.length) {
      setPreviewWorkoutItems(Array.isArray(workoutItems) ? workoutItems : []);
    }
    if (!previewActivePrograms.length || activePrograms.length > previewActivePrograms.length) {
      setPreviewActivePrograms(Array.isArray(activePrograms) ? activePrograms : []);
    }
  }, [currentWorkout?.dayBasedProgramId, workoutItems?.length, activePrograms?.length]);

  // Open carousel and use calendar workouts if available otherwise load next program workouts
  const handleAthleteViewClick = () => {
    if (Array.isArray(previewWorkoutItems) && previewWorkoutItems.length) {
      dispatch(setIsPreviewCarouselShowing(true));
      return;
    }
    if (Array.isArray(previewActivePrograms)
      && previewActivePrograms.length && selectedAthlete?.id && currentUser?.accountCode) {
      const program = previewActivePrograms[0];
      const nextSessionDay = (program?.trueDayNum ?? program?.dayNum);

      if (program?.dayBasedProgramId && nextSessionDay) {
        const dayNumZeroBased = Math.max(0, nextSessionDay - 1);

        dispatch(
          fetchProgramDayOne(
            currentUser,
            { dayBasedProgramId: program.dayBasedProgramId },
            dayNumZeroBased,
            selectedAthlete.id,
            program,
          ),
        );
      }
      dispatch(setIsPreviewCarouselShowing(true));
      return;
    }
    dispatch(setIsPreviewCarouselShowing(true));
  };

  const isPreviewLoading = isWorkoutsListLoading || isCalendarPreviewLoading;
  const shouldShowPreviewError = !isPreviewLoading && hasPreviewError && hasNoPreviewItems;
  const shouldShowPreviewEmpty = !isPreviewLoading && !hasPreviewError && hasNoPreviewItems;

  const getNextWorkoutLabel = () => {
    if (!nextWorkoutDate) return '';
    const workoutDate = parseISO(nextWorkoutDate);
    const today = new Date();
    const tomorrow = addDays(today, 1);

    if (isSameDay(workoutDate, today)) return 'Today';
    if (isSameDay(workoutDate, tomorrow)) return 'Tomorrow';
    return format(workoutDate, 'MMMM d');
  };

  const renderNextWorkoutHeader = () => {
    if (showNextWorkoutLoading) {
      return (
        <LoadingContainer>
          <Shimmer>
            <LoadingTitle />
          </Shimmer>
        </LoadingContainer>
      );
    }

    return (
      <Text
        styledTextProps={{ fontSize: '16px', fontWeight: 700 }}
        styledWrapperProps={{ marginBottom: '8px' }}
      >
        Next Calendar Workout:
        {' '}
        {getNextWorkoutLabel()}
      </Text>
    );
  };

  const renderPreviewLists = () => (
    <>
      {renderNextWorkoutHeader()}
      <WorkoutContent>
        <PreviewCalendarList
          workoutItems={previewWorkoutItems}
          isWorkoutsListLoading={isWorkoutsListLoading}
        />
      </WorkoutContent>
      <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
        Programs In Progress
      </Text>
      <WorkoutContent>
        <PreviewProgramList
          activePrograms={previewActivePrograms}
          expandedProgramKey={expandedProgramKey}
          setExpandedProgramKey={setExpandedProgramKey}
          programItems={programItems}
          isProgramItemsLoading={isProgramItemsLoading}
          currentUser={currentUser}
          selectedAthlete={selectedAthlete}
          dispatch={dispatch}
          fetchProgramItems={fetchProgramItems}
          loadingContentOffset={20}
        />
      </WorkoutContent>
    </>
  );

  let previewContent;
  if (shouldShowPreviewError) {
    previewContent = (
      <EmptyState>
        <EmptyIcon>
          <Icon size={32} icon='lift' color='#9E9E9E' />
        </EmptyIcon>
        <Text styledTextProps={{ fontSize: '16px', fontWeight: 700 }}>
          Error loading preview
        </Text>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          {previewErrorMessage}
        </Text>
      </EmptyState>
    );
  } else if (shouldShowPreviewEmpty) {
    previewContent = (
      <EmptyState>
        <EmptyIcon>
          <Icon size={32} icon='lift' color='#9E9E9E' />
        </EmptyIcon>
        <Text styledTextProps={{ fontSize: '16px', color: '#9E9E9E' }}>
          No scheduled workouts or active programs.
        </Text>
      </EmptyState>
    );
  } else {
    previewContent = renderPreviewLists();
  }

  return (
    <Container>
      <Header>
        <TitleContainer>
          <Text styledTextProps={{
            fontSize: '18px',
            fontWeight: 700,
            letterSpacing: '0.5px',
          }}
          >
            Preview
          </Text>
          <Button
            name="See Athlete's View"
            icon='mobile'
            iconColor='#fff'
            customBorderRadius='4px'
            noBorder
            bgColor='#444'
            txtColor='#fff'
            fontSize='16px'
            onClick={handleAthleteViewClick}
            disabled={hasNoPreviewItems}
            styledWrapperProps={{
              boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
              padding: '6px 16px',
            }}
            styledTextProps={{ fontWeight: '700', letterSpacing: '0.5px' }}
          />
        </TitleContainer>
      </Header>
      <ScrollContainer>
        {previewContent}
      </ScrollContainer>
    </Container>
  );
};

AthletePreview.propTypes = {
  selectedAthlete: PropTypes.shape({
    id: PropTypes.number,
  }),
};

AthletePreview.defaultProps = {
  selectedAthlete: null,
};

export default AthletePreview;
